import { BdsButton, BdsButtonIcon, BdsGrid, BdsIcon, BdsPaper, BdsTypo } from 'blip-ds/dist/blip-ds-react/components';
import { AlertHeaderVariannt } from 'blip-ds/dist/types/components/alert/alert-header/alert-header';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentRow } from '../../components/DocumentRow/DocumentRow';
import { Modal } from '../../components/Modal';
import { IToast } from '../../components/Toast';
import { Warning } from '../../components/Warning/Warning';
import { useAppContext } from '../../context/AppContext';
import { WARNING_TYPE } from '../../constants';
import { Category, ChangeDataParams, Reply, Warning as WarningProps, WarningTypes } from '../../interfaces';
import { deleteCategory, editReplies } from '../../services/CategoryService';
import './ReplyList.scss';

const init_value = { id: '', name: '', replies: [] };

const ReplyList = () => {
  const { t } = useTranslation();
  const { selectedCategory, setSelectedCategory, setPage, setCategories, categories, apiService, botList } =
    useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showWarning, setShowWarning] = useState<WarningTypes>(false);
  const [data, setData] = useState<Category>(init_value);
  const [errorRows, setErrorRows] = useState<Set<number>>(new Set());
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const formRef = useRef<HTMLBdsGridElement>(null);

  const empty_row = { name: '', document: '', isDynamicContent: false, type: 'text/plain', category: data.name };

  useEffect(() => {
    setData(
      selectedCategory.replies.length == 0 ? { ...selectedCategory, replies: [empty_row] } : { ...selectedCategory },
    );
  }, [selectedCategory]);

  useEffect(() => {
    setHasChanged(
      JSON.stringify(removeEmptyRows(data.replies)) !== JSON.stringify(removeEmptyRows(selectedCategory.replies)),
    );
  }, [data]);

  useEffect(() => {
    setHasError(errorRows.size > 0);
  }, [errorRows]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hasChanged, hasError]);

  const isLastDuplicate = (referenceReply: Reply, replies: Reply[]) => {
    if (referenceReply.name.length === 0) return false;
    const filteredReplies = replies.filter(reply => reply.name === referenceReply.name);
    return filteredReplies.length > 1 && filteredReplies[filteredReplies.length - 1] === referenceReply;
  };

  const handleChangeCategoryName = async (name: string) => {
    if (selectedCategory.replies.length > 0) {
      setLoading(true);
      const result = await editReplies(
        apiService,
        data.id || '',
        changeCategoryName(name, removeEmptyRows(data.replies)),
        botList,
      );
      if (result == 'success') {
        setCategories(prevCategories =>
          prevCategories.map(category => (category.id === data.id ? { ...category, name: name } : category)),
        );
        const newData = { ...data, name: name };
        setData(newData);
      }
      setLoading(false);
      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
      setData(prevData => ({ ...prevData, name: name }));
    }
  };

  const handleChangeData = ({ field, position, value }: ChangeDataParams) => {
    setData(prevData => {
      const updatedReplies = prevData.replies.map((reply, index) => {
        if (index === position) {
          return { ...reply, [field]: value };
        }
        return reply;
      });

      return { ...prevData, replies: updatedReplies };
    });
  };

  const handleAddRow = () => {
    setData(prevData => ({ ...prevData, replies: [...prevData.replies, { ...empty_row, category: data.name }] }));
  };

  const handleClose = () => {
    setPage('CategorySelection');
    setSelectedCategory({} as Category);
  };

  const removeEmptyRows = (replies: Reply[]): Reply[] => {
    return replies.filter(reply => reply.name.trim() !== '' && reply.document.trim() !== '');
  };

  const changeCategoryName = (name: string, replies: Reply[]) => {
    return replies.map(reply => ({ ...reply, category: name }));
  };

  const handleSave = async () => {
    if (!hasError) {
      setLoading(true);
      const treatedReplies = changeCategoryName(data.name, removeEmptyRows(data.replies));

      let newData = categories;
      let result: IToast = 'failed';
      if (removeEmptyRows(data.replies).length == 0) {
        result = await deleteCategory(apiService, data.id || '', botList);
        newData = categories.filter(category => category.id !== data.id);
      } else if (selectedCategory.replies.length > 0) {
        result = await editReplies(apiService, data.id || '', treatedReplies, botList);
        newData = categories.map(category =>
          category.id === data.id ? { ...data, replies: treatedReplies } : category,
        );
      } else {
        result = await editReplies(apiService, data.id || '', treatedReplies, botList);
        newData = [...categories, { ...data, replies: treatedReplies }];
      }
      if (result === 'success') {
        setCategories(newData);
        setSelectedCategory(data);
      }
      setLoading(false);
      setShowWarning(false);
    }
  };

  const handleDelete = (replyToDelete: Reply) => {
    setData(prevData => {
      const index = prevData.replies.findIndex(
        reply => reply.name === replyToDelete.name && reply.document === replyToDelete.document,
      );

      const newReplies = [...prevData.replies];
      newReplies.splice(index, 1);

      setErrorRows(prevErrors => {
        const newErrors = new Set(prevErrors);
        newErrors.delete(index);
        return newErrors;
      });

      return {
        ...prevData,
        replies: newReplies,
      };
    });
  };

  const handleRowErrorChange = (index: number, hasError: boolean) => {
    setErrorRows(prevErrors => {
      const newErrors = new Set(prevErrors);
      if (hasError) {
        newErrors.add(index);
      } else {
        newErrors.delete(index);
      }
      return newErrors;
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (formRef.current && !(formRef.current as unknown as Element).contains(target) && hasChanged && !loading) {
      if (hasError) {
        setShowWarning(WARNING_TYPE.ERROR_IN_CHANGES);
      } else {
        setShowWarning(WARNING_TYPE.SAVE_CHANGES);
      }
    }
  };

  const getWarningProps = () => {
    switch (showWarning) {
      case WARNING_TYPE.DISCARD_CHANGES:
        return {
          closeText: t('go-personal-reply-list-warning-back'),
          handleCloseButton: () => setShowWarning(false),
          handleSubmitButton: handleClose,
          text: t('go-personal-reply-list-warning-discard-text'),
          title: t('go-personal-reply-list-warning-discard-title'),
          submitText: t('go-personal-reply-list-warning-continue'),
          variant: 'warning' as AlertHeaderVariannt,
          isOpen: true,
          setIsOpen: e => setShowWarning(e),
        };
      case WARNING_TYPE.SAVE_CHANGES:
        return {
          closeText: t('go-personal-reply-list-warning-back'),
          handleCloseButton: () => setShowWarning(false),
          handleSubmitButton: handleSave,
          text: t('go-personal-reply-list-warning-save-text'),
          title: t('go-personal-reply-list-warning-save-title'),
          submitText: t('go-personal-reply-list-warning-save-button'),
          variant: 'warning' as AlertHeaderVariannt,
          isOpen: true,
          setIsOpen: e => setShowWarning(e),
          loading: loading,
        };
      case WARNING_TYPE.ERROR_IN_CHANGES:
        return {
          closeText: t('go-personal-reply-list-warning-back'),
          handleCloseButton: () => setShowWarning(false),
          text: t('go-personal-reply-list-warning-error-text'),
          title: t('go-personal-reply-list-warning-error-title'),
          variant: 'warning' as AlertHeaderVariannt,
          isOpen: true,
          setIsOpen: e => setShowWarning(e),
        };
      default:
        return {} as WarningProps;
    }
  };

  return (
    <BdsGrid xxs="12" padding="none" ref={formRef}>
      <BdsGrid direction="column" padding="none" gap="2" style={{ width: '100%' }}>
        <BdsPaper elevation="none" width="100%" class="reply-list-paper">
          <BdsGrid padding="none" direction="column" gap="none" justifyContent="center">
            <BdsGrid gap="2">
              <BdsTypo bold="bold" variant="fs-24" lineHeight="plus">
                {data.name}
              </BdsTypo>
              <BdsButtonIcon size="short" variant="secondary" icon="edit" onClick={() => setIsModalOpen(true)} />
            </BdsGrid>
            <BdsTypo>{t('go-personal-reply-list-description')}</BdsTypo>
            <BdsGrid padding="t-2" direction="column" gap="2" justifyContent="center">
              {data.replies &&
                data.replies.map((row, i) => (
                  <DocumentRow
                    data={row}
                    position={i}
                    handleDelete={e => handleDelete(e)}
                    handleChangeData={e => handleChangeData(e)}
                    onErrorChange={handleRowErrorChange}
                    key={i}
                    isUnique={data.replies.length === 1}
                    isDuplicated={isLastDuplicate(row, data.replies)}
                  />
                ))}
            </BdsGrid>
          </BdsGrid>
          <BdsGrid justifyContent="flex-end" padding="t-2">
            <BdsButton
              variant="text"
              icon="add"
              icon-left="add"
              color="content"
              icon-theme="outline"
              type-icon="icon"
              onClick={() => handleAddRow()}
            >
              {t('go-personal-reply-list-add-field')}
            </BdsButton>
          </BdsGrid>
          <BdsGrid justifyContent="space-between" padding="t-2" style={{ width: '100%' }}>
            <BdsButton
              variant="secondary"
              onClick={() => !loading && (hasChanged || hasError ? setShowWarning(WARNING_TYPE.DISCARD_CHANGES) : handleClose())}
              disabled={loading}
            >
              {t('go-personal-reply-list-back-button')}
            </BdsButton>
            <BdsGrid gap="2">
              {hasChanged && (
                <BdsGrid gap="1" alignItems="center">
                  <BdsIcon theme="outline" size="small" name="attention"></BdsIcon>
                  <BdsTypo variant="fs-14">{t('go-personal-reply-list-unsaved-changes')}</BdsTypo>
                </BdsGrid>
              )}
              <BdsButton
                icon="check"
                icon-left="check"
                disabled={!hasChanged}
                onBdsClick={() => !loading && handleSave()}
                bdsLoading={loading && !isModalOpen && !showWarning}
              >
                {t('go-personal-reply-list-save-button')}
              </BdsButton>
            </BdsGrid>
          </BdsGrid>
        </BdsPaper>
      </BdsGrid>
      <Modal
        buttonAction={e => handleChangeCategoryName(e)}
        button_text={t('go-personal-reply-list-modal-save-button')}
        input_label={t('go-personal-reply-list-modal-input-label')}
        open={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={t('go-personal-reply-list-modal-title', { categoryName: data.name })}
        max_length={60}
        loading={loading}
      />
      <Warning {...getWarningProps()} />
    </BdsGrid>
  );
};

export default ReplyList;
